import { useNotifications } from '@/hooks/useNotifications';
import { cn } from '@/lib/utils';
import { Bell, PackageSearch } from 'lucide-react';
import { useNavigate } from 'react-router';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';

interface NotificationDropdownProps {
  children: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
}

function relativeTime(dateString: string): string {
  const now = Date.now();
  const date = new Date(dateString).getTime();
  const diffSecs = Math.floor((now - date) / 1000);

  if (diffSecs < 60) return 'just now';

  const diffMins = Math.floor(diffSecs / 60);
  if (diffMins < 60) return `${diffMins}m ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'yesterday';
  return `${diffDays}d ago`;
}

export function NotificationDropdown({ children, onOpenChange }: NotificationDropdownProps) {
  const { items, loading, markRead, markAllRead, unreadCount } = useNotifications();
  const navigate = useNavigate();

  const hasUnread = unreadCount > 0;

  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        {/* Header */}
        <div className="flex items-center justify-between px-2 py-1">
          <DropdownMenuLabel className="text-base">Notifications</DropdownMenuLabel>
          {hasUnread && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto px-2 py-1 text-xs font-normal text-primary hover:text-primary/80"
              onClick={markAllRead}
            >
              Mark all read
            </Button>
          )}
        </div>
        <DropdownMenuSeparator />

        {/* Loading state */}
        {loading && (
          <div className="space-y-3 p-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-1.5">
                <Skeleton className="h-4 w-3/5" />
                <Skeleton className="h-3 w-full" />
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && items.length === 0 && (
          <div className="flex flex-col items-center gap-2 px-4 py-8 text-center text-muted-foreground">
            <Bell className="h-8 w-8" />
            <p className="text-sm">No notifications yet</p>
          </div>
        )}

        {/* Notification list */}
        {!loading && items.length > 0 && (
          <ScrollArea className="max-h-80">
            {items.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className={cn(
                  'flex flex-col items-start gap-0.5 px-3 py-2.5',
                  'cursor-pointer focus:bg-accent',
                  !notification.read && 'bg-blue-50/50 dark:bg-blue-950/10',
                )}
                onClick={() => {
                  if (!notification.read) {
                    markRead(notification.id);
                  }
                }}
              >
                <div className="flex w-full items-start gap-2">
                  {/* Blue dot for unread */}
                  {!notification.read && (
                    <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-fos-accent" />
                  )}
                  {/* Dot spacer for read (keeps text aligned) */}
                  {notification.read && <span className="mt-1.5 h-2 w-2 flex-shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p
                        className={cn(
                          'truncate text-sm',
                          !notification.read
                            ? 'font-semibold text-foreground'
                            : 'font-medium text-foreground',
                        )}
                      >
                        {notification.title}
                      </p>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {relativeTime(notification.created_at)}
                      </span>
                    </div>
                    <p className="line-clamp-2 text-xs text-muted-foreground">
                      {notification.body}
                    </p>
                    {/* Sprint 16 Task C: low_stock notifications get a deep-link
                        to the filtered Inventory view so the seller can act
                        without hunting for the category. */}
                    {notification.type === 'low_stock' && (
                      <button
                        type="button"
                        className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          const category = (notification.metadata as Record<string, unknown> | null)
                            ?.category;
                          if (typeof category === 'string' && category) {
                            navigate(`/inventory?category=${encodeURIComponent(category)}`);
                          } else {
                            navigate('/inventory');
                          }
                        }}
                      >
                        <PackageSearch className="h-3 w-3" />
                        View category
                      </button>
                    )}
                  </div>
                </div>
              </DropdownMenuItem>
            ))}
          </ScrollArea>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
